// Leaderboard Types
// Backend API: GET /api/v1/leaderboard

export type UserTier = 'diamond' | 'platinum' | 'gold' | 'silver' | 'bronze' | 'seedling';
export type LeaderboardCategory = 'most_trades' | 'most_cards_sent' | 'most_points' | 'highest_rated' | 'rising_stars' | 'new_members';
export type LeaderboardPeriod = 'week' | 'month' | 'all_time';

// Tier thresholds (based on all-time trades)
export const TIER_THRESHOLDS: Record<UserTier, { min: number; max: number | null }> = {
  diamond: { min: 100, max: null },
  platinum: { min: 75, max: 99 },
  gold: { min: 50, max: 74 },
  silver: { min: 25, max: 49 },
  bronze: { min: 10, max: 24 },
  seedling: { min: 0, max: 9 },
};

// Tier display configuration
export const TIER_CONFIG: Record<UserTier, { label: string; color: string }> = {
  diamond: { label: 'Diamond', color: '#b9f2ff' },
  platinum: { label: 'Platinum', color: '#e5e4e2' },
  gold: { label: 'Gold', color: '#ffd700' },
  silver: { label: 'Silver', color: '#c0c0c0' },
  bronze: { label: 'Bronze', color: '#cd7f32' },
  seedling: { label: 'Seedling', color: '#90EE90' },
};

// Category display configuration
export const CATEGORY_CONFIG: Record<LeaderboardCategory, { label: string; statLabel: string }> = {
  most_trades: { label: 'Most Trades', statLabel: 'trades' },
  most_cards_sent: { label: 'Most Cards Sent', statLabel: 'cards' },
  most_points: { label: 'Most Points', statLabel: 'points' },
  highest_rated: { label: 'Highest Rated', statLabel: 'rating' },
  rising_stars: { label: 'Rising Stars', statLabel: 'growth' },
  new_members: { label: 'New Members', statLabel: 'wants' },
};

// Period display configuration
export const PERIOD_CONFIG: Record<LeaderboardPeriod, { label: string }> = {
  week: { label: 'This Week' },
  month: { label: 'This Month' },
  all_time: { label: 'All Time' },
};

// Individual leaderboard entry from backend
export interface LeaderboardEntry {
  rank: number;
  id: number;
  username: string;
  avatarUrl: string | null;
  country: string | null;
  tier: UserTier;
  totalTrades: number;
  displayValue: number;
  points: number;
  memberSince: string;
  wantListCount?: number; // Populated for new_members and most_points categories
}

// Current user's rank info (for "Your Rank" callout)
export interface CurrentUserRank {
  rank: number | null;
  tier: UserTier;
  displayValue: number;
}

// API response structure
export interface LeaderboardResponse {
  podium: LeaderboardEntry[];
  rankings: LeaderboardEntry[];
  currentUser: CurrentUserRank | null;
  totalCount: number;
  hasMore: boolean;
  page: number;
  limit: number;
}

// Query parameters for API
export interface LeaderboardQueryParams {
  category: LeaderboardCategory;
  period: LeaderboardPeriod;
  country?: string;
  search?: string;
  page?: number;
  limit?: number;
  minWantListCount?: number;
}
